// SSE client for the streaming chat Function URL (Task 4.1, D-A4).
//
// The Function URL always responds HTTP 200 (RESPONSE_STREAM has no way to send
// a real 401 mid-stream — api/src/aiChat.ts:223-231 fixes the status before the
// JWT is even checked), so an expired token surfaces as the FIRST parsed SSE
// event being {type:"error",code:"UNAUTHORIZED"}. That is what triggers the
// one-shot refresh + retry here, mirroring web/src/lib/api.ts:89-100.

import type { AiChatContextRef, AiChatEvent, AiChatRequest } from "@checklist/shared";
import { getAccessToken, refreshSession } from "../auth.js";

const CHAT_URL = (import.meta.env.VITE_AI_CHAT_URL as string | undefined)?.replace(/\/+$/, "");

export function isAiChatConfigured(): boolean {
  return Boolean(CHAT_URL);
}

export interface StreamChatArgs {
  sessionId?: string;
  forceNew?: boolean;
  message: string;
  context: AiChatContextRef | null;
}

export interface StreamChatHandle {
  abort: () => void;
}

function parseFrame(frame: string): AiChatEvent | null {
  const dataLine = frame.split("\n").find((line) => line.startsWith("data: "));
  if (!dataLine) return null;
  try {
    return JSON.parse(dataLine.slice("data: ".length)) as AiChatEvent;
  } catch {
    return null;
  }
}

async function* iterateFrames(res: Response): AsyncGenerator<AiChatEvent> {
  const reader = res.body?.getReader();
  if (!reader) return;
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (value) buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const event = parseFrame(frame);
      if (event) yield event;
    }
    if (done) return;
  }
}

function postOnce(args: StreamChatArgs, token: string, signal: AbortSignal): Promise<Response> {
  const body: AiChatRequest = {
    sessionId: args.sessionId,
    forceNew: args.forceNew,
    message: args.message,
    context: args.context,
  };
  return fetch(CHAT_URL!, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
    signal,
  });
}

/** Stream one chat turn over SSE, calling onEvent for each parsed AiChatEvent. */
export function streamChat(
  args: StreamChatArgs,
  onEvent: (event: AiChatEvent) => void
): StreamChatHandle {
  const controller = new AbortController();

  void (async () => {
    if (!CHAT_URL) {
      onEvent({ type: "error", code: "NOT_CONFIGURED", message: "AI chat is not configured." });
      onEvent({ type: "done" });
      return;
    }
    const token = getAccessToken();
    if (!token) {
      onEvent({ type: "error", code: "UNAUTHORIZED", message: "Sign in to use the assistant." });
      onEvent({ type: "done" });
      return;
    }

    try {
      let res = await postOnce(args, token, controller.signal);
      let iter = iterateFrames(res);
      let first = await iter.next();

      if (!first.done && first.value.type === "error" && first.value.code === "UNAUTHORIZED") {
        const refreshed = await refreshSession().catch(() => false);
        const retryToken = refreshed ? getAccessToken() : null;
        if (!retryToken) {
          onEvent(first.value);
          onEvent({ type: "done" });
          return;
        }
        res = await postOnce(args, retryToken, controller.signal);
        iter = iterateFrames(res);
        first = await iter.next();
      }

      // The server ends a turn with "done", or with "error" and no "done" (fail()).
      let sawTerminal = false;
      const deliver = (event: AiChatEvent) => {
        if (event.type === "done" || event.type === "error") sawTerminal = true;
        onEvent(event);
      };
      if (!first.done) deliver(first.value);
      for (;;) {
        const next = await iter.next();
        if (next.done) break;
        deliver(next.value);
      }
      // The stream closed with neither: the Lambda timed out or crashed mid-turn.
      // Without this the panel would stay "streaming" with the waiting dots running
      // forever (site-assistant SA24).
      if (!sawTerminal && !controller.signal.aborted) {
        onEvent({ type: "error", code: "STREAM_ENDED", message: "The assistant stopped responding. Please try again." });
        onEvent({ type: "done" });
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      const message = err instanceof Error ? err.message : String(err);
      onEvent({ type: "error", code: "NETWORK_ERROR", message });
      onEvent({ type: "done" });
    }
  })();

  return { abort: () => controller.abort() };
}
